import { Bot, Star, User, type LucideIcon } from 'lucide-react';
import type * as React from 'react';

import { cn } from '../lib/utils';

/** User-type discriminator. */
export type UserTypeIconType = 'internal' | 'external' | 'serviceAccount';

/** Optional star marker overlay; color differs per marker semantic. */
export type UserTypeIconMarker = 'favorite' | 'privileged' | 'external';

export interface UserTypeIconProps {
  type: UserTypeIconType;
  /** When set, renders a small star overlay in the bottom-right corner. */
  marker?: UserTypeIconMarker;
  className?: string;
  /** Optional aria-label. When omitted, the glyph is decorative (`aria-hidden`). */
  'aria-label'?: string;
}

// `external` shares the plain `User` glyph with `internal` per Figma `5:23254`
// — semantic differentiation is by color (red `text-error`), not glyph shape.
const TYPE_TO_GLYPH = {
  internal: User,
  external: User,
  serviceAccount: Bot,
} as const satisfies Readonly<Record<UserTypeIconType, LucideIcon>>;

/**
 * Marker color tokens — favorite = yellow tailwind, privileged = red tailwind,
 * external = empty-string bridge (color carried by `MARKER_INLINE_STYLE` +
 * `MARKER_COLOR_TOKEN` per the codified `UserTypeBadge` bridge pattern in
 * `.claude/cc10x/v10/patterns.md`). When `dawn-yellow` is added to
 * `tailwind.config.ts extend.colors`, the `external` entry flips to
 * `'text-dawn-yellow'` and the inline-style + data-color-token maps drop.
 */
const MARKER_COLOR = {
  favorite: 'text-yellow-500',
  privileged: 'text-error',
  external: '',
} as const satisfies Readonly<Record<UserTypeIconMarker, string>>;

/**
 * Inline-style overrides for markers whose brand color is not yet a tailwind
 * token. Per design §5.2 #24 (LOCKED 2026-05-20): `external` uses Dawn
 * Yellow `#FFCA27` via inline style so the tailwind config can be migrated
 * without a coupling cascade.
 */
const MARKER_INLINE_STYLE: Partial<Record<UserTypeIconMarker, React.CSSProperties>> = {
  external: { color: '#FFCA27' },
};

/**
 * Greppable migration anchor — every marker rendered with an inline-style
 * brand color carries this attribute. When the underlying tailwind token is
 * promoted, search-replace removes both the inline style and this attribute.
 */
const MARKER_COLOR_TOKEN: Partial<Record<UserTypeIconMarker, string>> = {
  external: 'dawn-yellow',
};

/**
 * UserTypeIcon — internal / external / serviceAccount @ 18px per Figma registry
 * (design.md § 3.2). Optional star marker overlay (favorite or privileged) is
 * positioned at the bottom-right corner of the glyph.
 */
export const UserTypeIcon: React.FC<UserTypeIconProps> = ({
  type,
  marker,
  className,
  'aria-label': ariaLabel,
}) => {
  const Glyph = TYPE_TO_GLYPH[type];

  if (!marker) {
    return (
      <Glyph
        width={18}
        height={18}
        className={cn(className)}
        aria-label={ariaLabel}
        aria-hidden={ariaLabel ? undefined : true}
      />
    );
  }

  // With a marker, wrap so the Star can absolutely position over the glyph.
  return (
    <span
      className={cn('relative inline-block', className)}
      style={{ width: 18, height: 18 }}
      aria-label={ariaLabel}
      aria-hidden={ariaLabel ? undefined : true}
      role={ariaLabel ? 'img' : undefined}
    >
      <Glyph width={18} height={18} aria-hidden="true" />
      <Star
        data-user-type-marker
        data-color-token={MARKER_COLOR_TOKEN[marker]}
        width={8}
        height={8}
        className={cn('absolute -bottom-0.5 -right-0.5', MARKER_COLOR[marker])}
        style={MARKER_INLINE_STYLE[marker]}
        fill="currentColor"
        aria-hidden="true"
      />
    </span>
  );
};

UserTypeIcon.displayName = 'UserTypeIcon';
